<script setup>
import { useAlert } from 'dashboard/composables';
import MacrosTableRow from './MacrosTableRow.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showDeleteConfirmationPopup = ref(false);
const selectedMacro = ref({});
const searchQuery = ref('');

const records = computed(() => getters['macros/getMacros'].value);
const uiFlags = computed(() => getters['macros/getUIFlags'].value);

// Filtered macros based on search
const filteredRecords = computed(() => {
  if (!searchQuery.value) return records.value;
  const query = searchQuery.value.toLowerCase();
  return records.value.filter(macro =>
    macro.name.toLowerCase().includes(query)
  );
});

const deleteMessage = computed(() => ` ${selectedMacro.value.name}?`);

onMounted(() => {
  store.dispatch('macros/get');
});

const deleteMacro = async id => {
  try {
    await store.dispatch('macros/delete', id);
    useAlert(t('MACROS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('MACROS.DELETE.API.ERROR_MESSAGE'));
  }
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedMacro.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteMacro(selectedMacro.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('MACROS.LIST.TABLE_HEADER.NAME'),
    t('MACROS.LIST.TABLE_HEADER.CREATED BY'),
    t('MACROS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('MACROS.LIST.TABLE_HEADER.VISIBILITY'),
  ];
});
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="settings-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ $t('MACROS.HEADER') }}
      </h1>
      <router-link :to="{ name: 'macros_new' }">
        <button class="primary-action-btn">
          <span class="i-ph-plus-bold size-4" />
          {{ $t('MACROS.HEADER_BTN_TXT') }}
        </button>
      </router-link>
    </header>

    <!-- Description -->
    <div class="ws-desc-bar px-6 py-3">
      <p class="text-sm" style="color: #4b5563">
        {{ $t('MACROS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search and Filters -->
    <div class="ws-desc-bar px-6 py-3">
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-xs">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            placeholder="Search macros..."
          />
        </div>
        <div class="text-sm self-center" style="color: #6b7280">
          {{ filteredRecords.length }} macros
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="$t('MACROS.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredRecords.length"
        class="flex items-center justify-center py-20"
      >
        <p style="color: #6b7280">{{ $t('MACROS.LIST.404') }}</p>
      </div>

      <!-- Macros Table -->
      <table v-else class="w-full">
        <thead>
          <tr class="ws-thead-row">
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="px-6 py-3"
            >
              {{ thHeader }}
            </th>
            <th class="px-6 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="ws-tbody">
          <MacrosTableRow
            v-for="(macro, index) in filteredRecords"
            :key="index"
            :macro="macro"
            @delete="openDeletePopup(macro)"
          />
        </tbody>
      </table>
    </div>

    <!-- Delete Modal -->
    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('MACROS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('MACROS.DELETE.CONFIRM.YES')"
      :reject-text="$t('MACROS.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}

.settings-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb !important;
}

.ws-toolbar,
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}

.ws-content {
  background: #ffffff;
}

.ws-search,
.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}

.ws-search:focus,
.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  background: #ffffff !important;
}

.ws-search::placeholder,
.search-input::placeholder {
  color: #9ca3af !important;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.primary-action-btn:hover {
  background: #1f2937;
}

.primary-action-btn:active {
  transform: scale(0.98);
}

.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ws-thead-row th {
  padding: 10px 24px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151 !important;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.ws-tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s ease;
}

.ws-tbody tr:hover {
  background: #fafafa;
}

.ws-tbody td {
  color: #111827 !important;
  padding: 14px 24px;
  font-size: 13px;
  vertical-align: middle;
}

/* Delete modal in light style */
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
</style>
